import { MARKETING_CONFIG } from './marketing';

export const SEO_CONFIG = {
  siteUrl: 'https://elefa.tel',
  siteName: 'elefa Connect',
  legalName: 'ELEFA MOBILE PTY LTD',
  acn: '694 346 088',
  abn: '16 694 346 088',
  defaultTitle: 'elefa Connect | Call Any Phone Number, Anywhere',
  defaultDescription: `Call mobiles, landlines and basic phones in 195 countries. Encrypted in transit. Purchased credit never expires. $1 free to start.`,
  keywords: {
    brand: ['elefa connect', 'elefa', 'elefa calling app', 'elefa international calls'],
    international: ['international calling app', 'cheap international calls', 'voip calling app', 'OTT VoIP', 'call any phone number', 'call landlines from app'],
    diaspora: ['international calling', 'call family overseas', 'call abroad app', 'expat calling app', 'migrant calling app'],
    connect: ['elefa connect', 'international calling app', 'voip calling', 'no expiry calling credit', 'call any phone number'],
  },
} as const;

export const PAGE_SEO = {
  home: {
    title: 'elefa Connect | Call Any Phone Number, Anywhere',
    description: `Ring real phone numbers in every country. not just other app users. Encrypted in transit. Purchased credit never expires.`,
    keywords: [...SEO_CONFIG.keywords.brand, ...SEO_CONFIG.keywords.international].join(', '),
  },
  about: {
    title: 'About elefa Connect | International Calling App',
    description: `elefa Connect lets anyone, anywhere call any phone number worldwide. Encrypted in transit. Purchased credit never expires.`,
    keywords: [...SEO_CONFIG.keywords.brand, 'about elefa', 'international calling company'].join(', '),
  },
  rates: {
    title: 'International Calling Rates | elefa Connect',
    description: `Per-minute rates to every country, shown before every call. Starting from ${MARKETING_CONFIG.lowestInternationalRate}/min. No connection fees, no monthly plans.`,
    keywords: ['international calling rates', 'cheap calling rates', ...SEO_CONFIG.keywords.diaspora].join(', '),
  },
  connect: {
    title: 'elefa Connect Portal | Manage Your International Calling',
    description: `Sign in to the elefa Connect portal to top up credit, view rates and dial any phone number worldwide.`,
    keywords: SEO_CONFIG.keywords.connect.join(', '),
  },
  help: {
    title: 'Help & FAQ | elefa Connect',
    description: 'Get help with your elefa Connect account. FAQs, billing, calling support and troubleshooting.',
    keywords: 'elefa help, elefa faq, elefa support, elefa connect help',
  },
  howItWorks: {
    title: 'How It Works | elefa Connect',
    description: 'Sign up, top up credit, and call any phone number in any country. Encrypted in transit. Purchased credit never expires.',
    keywords: 'how elefa works, voip setup, international calling setup',
  },
  radio: {
    title: 'Free Radio | elefa Connect',
    description: 'Listen to free radio stations from around the world while you stay connected with elefa.',
    keywords: 'free radio, internet radio, world radio',
  },
  support: {
    title: 'Support | elefa Connect',
    description: 'Contact elefa Connect support. We\'re here to help with account, billing, and calling issues.',
    keywords: 'elefa support, contact elefa, elefa help',
  },
  referral: {
    title: 'Refer & Earn | elefa Connect',
    description: `Refer friends to elefa Connect and both get $${(MARKETING_CONFIG.referralBonusReferrer / 100).toFixed(0)} calling credit.`,
    keywords: 'elefa referral, refer a friend, calling credit bonus',
  },
  privacy: {
    title: 'Privacy Policy | elefa Connect',
    description: 'How elefa Connect protects your personal information and calling data. Your privacy matters.',
    keywords: 'elefa privacy policy, data protection',
  },
  terms: {
    title: 'Terms of Service | elefa Connect',
    description: 'Terms and conditions for using elefa Connect international calling service.',
    keywords: 'elefa terms, terms of service',
  },
  refunds: {
    title: 'Refunds | elefa Connect',
    description: 'When elefa Connect refunds purchased calling credit, how refunds are paid, and your Australian Consumer Law rights.',
    keywords: 'elefa refunds, calling credit refund, refund policy',
  },
  fairUse: {
    title: 'Fair Use Policy | elefa Connect',
    description: 'elefa Connect fair use policy for international calling credit and services.',
    keywords: 'elefa fair use, usage policy',
  },
  complaints: {
    title: 'Complaints Policy | elefa Connect',
    description: 'How to lodge a complaint with elefa Connect. We take your feedback seriously.',
    keywords: 'elefa complaints, feedback, dispute resolution',
  },
  hardship: {
    title: 'Financial Hardship | elefa Connect',
    description: 'elefa Connect financial hardship support. We\'re here to help during tough times.',
    keywords: 'financial hardship, payment support, elefa help',
  },
  cookies: {
    title: 'Cookie Policy | elefa Connect',
    description: 'How elefa Connect uses cookies and similar technologies on our website.',
    keywords: 'elefa cookies, cookie policy',
  },
  callHome: {
    title: 'Call Anyone, Anywhere | elefa Connect',
    description: `Ring landlines, mobiles and basic phones in every country. Encrypted in transit. Purchased credit never expires.`,
    keywords: 'international calling app, call any phone, call abroad, voip calling, no expiry calling credit',
  },
  saveOnCalls: {
    title: 'Per-Minute International Rates | elefa Connect',
    description: 'See the per-minute rate before every call. No contracts, no monthly fees. Purchased credit never expires.',
    keywords: 'cheap international calls, international call rates, per-minute calling, prepaid international',
  },
  noExpiry: {
    title: 'Calling Credit That Never Expires | elefa Connect',
    description: 'Purchased elefa Connect credit never expires. Top up once, use it whenever. Promotional credit may expire.',
    keywords: 'calling credit no expiry, prepaid credit never expires, international calling credit, no expiry voip',
  },
  hdCalling: {
    title: 'HD International Calls | elefa Connect',
    description: 'Call any phone number in any country over WiFi or data. Wideband HD voice, encrypted in transit.',
    keywords: 'international calls, hd voice, voip, wideband calling',
  },
  regulatory: {
    title: 'Regulatory Information | elefa Connect',
    description: 'elefa Connect regulatory posture and telecommunications information.',
    keywords: 'elefa regulatory, telecommunications, OTT VoIP',
  },
} as const;

export function getCanonicalUrl(path: string): string {
  return `${SEO_CONFIG.siteUrl}${path}`;
}

export function getBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SEO_CONFIG.siteName,
  legalName: SEO_CONFIG.legalName,
  url: SEO_CONFIG.siteUrl,
  logo: `${SEO_CONFIG.siteUrl}/favicon.png`,
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: MARKETING_CONFIG.contact.phoneInternational,
    contactType: 'customer service',
    areaServed: ['AU', 'US', 'GB', 'CA', 'DE', 'FR', 'AE', 'NZ'],
    availableLanguage: ['English'],
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Melbourne',
    addressCountry: 'AU',
  },
};

export const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SEO_CONFIG.siteName,
  url: SEO_CONFIG.siteUrl,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SEO_CONFIG.siteUrl}/connect-portal/rates?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

export const TELECOM_SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'elefa Connect International Calling',
  provider: ORGANIZATION_SCHEMA,
  serviceType: 'International VoIP Calling',
  areaServed: ['AU', 'US', 'GB', 'CA', 'DE', 'FR', 'AE', 'NZ'],
  description: SEO_CONFIG.defaultDescription,
  offers: {
    '@type': 'Offer',
    price: '0.05',
    priceCurrency: 'AUD',
    description: `International calls from ${MARKETING_CONFIG.lowestInternationalRate}/min`,
  },
};

export const SOFTWARE_APP_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'elefa Connect',
  applicationCategory: 'CommunicationApplication',
  operatingSystem: 'iOS, Android, Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'AUD',
  },
  description: SEO_CONFIG.defaultDescription,
};
